import React, { useCallback, useRef, useState } from 'react';
import { contentPointToOverlay, notifySheetImagesMutated } from '../../utils/spreadsheetImageHelpers';

const MIN_SIZE = 40;

function applyImagePatch(spreadsheetInstanceRef, imageId, patch) {
  const data = spreadsheetInstanceRef?.current?.sheet?.data;
  if (!data) return;
  if (!Array.isArray(data.images)) data.images = [];
  data.changeData(() => {
    data.images = data.images.map((im) => (im.id === imageId ? { ...im, ...patch } : im));
  });
  spreadsheetInstanceRef.current?.sheet?.table?.render?.();
  notifySheetImagesMutated();
}

export default function ImageItem({ image, spreadsheetInstanceRef, isReadOnly, layoutRev }) {
  const data = spreadsheetInstanceRef?.current?.sheet?.data;
  const { left, top } = contentPointToOverlay(data, image.x, image.y);

  const aspectRef = useRef(image.width / Math.max(image.height, 1));
  const dragRef = useRef(null);

  const [dragPreview, setDragPreview] = useState(null);
  const [resizePreview, setResizePreview] = useState(null);

  const display = resizePreview || dragPreview || {
    x: left,
    y: top,
    w: image.width,
    h: image.height,
  };

  const onPointerDownDrag = useCallback(
    (e) => {
      if (isReadOnly || e.button !== 0) return;
      e.stopPropagation();
      e.preventDefault();
      const startClientX = e.clientX;
      const startClientY = e.clientY;
      const startX = image.x;
      const startY = image.y;
      dragRef.current = { startClientX, startClientY, startX, startY };

      const onMove = (ev) => {
        if (!dragRef.current) return;
        const dx = ev.clientX - dragRef.current.startClientX;
        const dy = ev.clientY - dragRef.current.startClientY;
        const d = spreadsheetInstanceRef?.current?.sheet?.data;
        if (!d) return;
        const p0 = contentPointToOverlay(d, dragRef.current.startX, dragRef.current.startY);
        const p1 = contentPointToOverlay(d, dragRef.current.startX + dx, dragRef.current.startY + dy);
        const ox = p1.left - p0.left;
        const oy = p1.top - p0.top;
        setDragPreview({
          x: left + ox,
          y: top + oy,
          w: image.width,
          h: image.height,
        });
      };

      const onUp = (ev) => {
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
        if (!dragRef.current) return;
        const dx = ev.clientX - dragRef.current.startClientX;
        const dy = ev.clientY - dragRef.current.startClientY;
        dragRef.current = null;
        setDragPreview(null);
        applyImagePatch(spreadsheetInstanceRef, image.id, {
          x: startX + dx,
          y: startY + dy,
        });
      };

      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
    },
    [isReadOnly, image, left, top, spreadsheetInstanceRef, layoutRev]
  );

  const startResize = useCallback(
    (corner, e) => {
      if (isReadOnly || e.button !== 0) return;
      e.stopPropagation();
      e.preventDefault();
      const start = {
        cx: e.clientX,
        cy: e.clientY,
        x: image.x,
        y: image.y,
        w: image.width,
        h: image.height,
        aspect: image.width / Math.max(image.height, 1),
        corner,
      };
      aspectRef.current = start.aspect;

      const onMove = (ev) => {
        const ddx = ev.clientX - start.cx;
        const ddy = ev.clientY - start.cy;
        const lockAspect = !ev.shiftKey;
        let { x, y, w, h } = start;

        if (corner === 'se') {
          if (lockAspect) {
            const nw = Math.max(MIN_SIZE, start.w + ddx);
            const nh = Math.max(MIN_SIZE, nw / aspectRef.current);
            w = nw;
            h = nh;
          } else {
            w = Math.max(MIN_SIZE, start.w + ddx);
            h = Math.max(MIN_SIZE, start.h + ddy);
          }
        } else if (corner === 'nw') {
          if (lockAspect) {
            const right = start.x + start.w;
            const bottom = start.y + start.h;
            const nw = Math.max(MIN_SIZE, start.w - ddx);
            const nh = Math.max(MIN_SIZE, nw / aspectRef.current);
            x = right - nw;
            y = bottom - nh;
            w = nw;
            h = nh;
          } else {
            const nx = start.x + ddx;
            const ny = start.y + ddy;
            const nw = Math.max(MIN_SIZE, start.w - ddx);
            const nh = Math.max(MIN_SIZE, start.h - ddy);
            x = nx;
            y = ny;
            w = nw;
            h = nh;
          }
        } else if (corner === 'ne') {
          if (lockAspect) {
            const left0 = start.x;
            const bottom = start.y + start.h;
            const nw = Math.max(MIN_SIZE, start.w + ddx);
            const nh = Math.max(MIN_SIZE, nw / aspectRef.current);
            x = left0;
            y = bottom - nh;
            w = nw;
            h = nh;
          } else {
            w = Math.max(MIN_SIZE, start.w + ddx);
            h = Math.max(MIN_SIZE, start.h - ddy);
            y = start.y + ddy;
          }
        } else if (corner === 'sw') {
          if (lockAspect) {
            const right = start.x + start.w;
            const top0 = start.y;
            const nw = Math.max(MIN_SIZE, start.w - ddx);
            const nh = Math.max(MIN_SIZE, nw / aspectRef.current);
            x = right - nw;
            y = top0;
            w = nw;
            h = nh;
          } else {
            w = Math.max(MIN_SIZE, start.w - ddx);
            h = Math.max(MIN_SIZE, start.h + ddy);
            x = start.x + ddx;
          }
        }

        const d = spreadsheetInstanceRef?.current?.sheet?.data;
        if (!d) return;
        const p = contentPointToOverlay(d, x, y);
        setResizePreview({ x: p.left, y: p.top, w, h, content: { x, y, width: w, height: h } });
      };

      const onUp = () => {
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
        setResizePreview((prev) => {
          if (prev?.content) {
            applyImagePatch(spreadsheetInstanceRef, image.id, prev.content);
          }
          return null;
        });
      };

      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
    },
    [isReadOnly, image, spreadsheetInstanceRef]
  );

  return (
    <div
      className={`milegrid-image-item ${isReadOnly ? 'milegrid-image-item-readonly' : 'milegrid-image-item-interactive'}`}
      style={{
        left: display.x,
        top: display.y,
        width: display.w,
        height: display.h,
      }}
      onPointerDown={isReadOnly ? undefined : onPointerDownDrag}
      role="presentation"
    >
      <img src={image.src} alt="" draggable={false} />
      {!isReadOnly && (
        <>
          <div
            className="milegrid-image-resize-handle milegrid-image-resize-se"
            onPointerDown={(e) => startResize('se', e)}
            role="presentation"
          />
          <div
            className="milegrid-image-resize-handle milegrid-image-resize-nw"
            onPointerDown={(e) => startResize('nw', e)}
            role="presentation"
          />
          <div
            className="milegrid-image-resize-handle milegrid-image-resize-ne"
            onPointerDown={(e) => startResize('ne', e)}
            role="presentation"
          />
          <div
            className="milegrid-image-resize-handle milegrid-image-resize-sw"
            onPointerDown={(e) => startResize('sw', e)}
            role="presentation"
          />
        </>
      )}
    </div>
  );
}
